import { createSlice } from "@reduxjs/toolkit";
import { sendMassMessage, scheduleMassMessage, fetchGroupMessageFilters, fetchMassSmsBudget, fetchMassMessageHistory, fetchAdminMassMessageHistory, fetchAdminMassMessageRecipients, cancelMassMessage, updateMassMessage, composeMassMessage, fetchAiTones, fetchMassMessageSends, fetchPastMassMessageRecipients } from "./MassMessageActions";

const initialState = {
  loading:         false,
  filterLoading:   true,
  filterError:     "",
  locationOptions: [],
  positionOptions: [],
  employeeOptions: [],
  categories:      [],
  smsBudget:       null,
  history:         { active: [], inactive: [], loading: false, error: "" },
  // Admin-only super-set of `history` (TBAC `Mass Message — Admin` row, server
  // side). The /mass-message-admin page reads from here; per-user history
  // panel still reads from `history` — separate so views don't pollute.
  adminHistory:    { active: [], inactive: [], loading: false, error: "", loaded: false },
  // Tracks an in-flight updateMassMessage by recordId for Save button disable.
  updatingId:      null,
  // AI compose preview state. `draft` holds the generated text waiting for the
  // user to accept/regenerate/discard; we never auto-apply it to the message
  // textarea so the user always reviews before send.
  aiCompose:       { loading: false, error: "", draft: "", finishReason: "", truncated: false, lastPrompt: "" },
  // Live AI Tone choices — fetched from /comm/portal/get/ai-tones on mount.
  // Keep `loaded` separate from `loading` so the UI can distinguish "haven't
  // tried yet" from "tried, got an empty list" (the latter typically means a
  // BFF / Airtable failure and is surfaced via `error`).
  aiTones:         { loading: false, loaded: false, error: "", list: [] },
  // Per-fire send history keyed by Group Messages parent id. Lazy-loaded when
  // a recurring history item is expanded so we don't N+1 the BFF on mount —
  // most users have a handful of records but only inspect one at a time.
  sendsByGroupId:  {},
  // Live cohort resolution keyed by Group Messages parent id. Populated when
  // an admin clicks "Show recipients" on an active card; the BFF runs the
  // same filter the cron will use at fire time so the preview is exact.
  recipientsByMessageId: {},
  // Phase 5: snapshotted past recipients keyed by Group Messages parent id.
  // Populated when an admin clicks "Show recipients" on an inactive (sent/cancelled) card.
  pastRecipientsByMessageId: {},
};

const MassMessageSlice = createSlice({
  name: "MassMessage",
  initialState,
  reducers: {
    // Discard the AI draft (e.g. when the user edits the textarea or sends).
    clearAiDraft(state) {
      state.aiCompose.draft        = "";
      state.aiCompose.error        = "";
      state.aiCompose.finishReason = "";
      state.aiCompose.truncated    = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── sendMassMessage ──────────────────────────────────────────────
      .addCase(sendMassMessage.pending,   (state) => { state.loading = true; })
      .addCase(sendMassMessage.fulfilled, (state) => { state.loading = false; })
      .addCase(sendMassMessage.rejected,  (state) => { state.loading = false; })

      // ── scheduleMassMessage ──────────────────────────────────────────
      .addCase(scheduleMassMessage.pending,   (state) => { state.loading = true; })
      .addCase(scheduleMassMessage.fulfilled, (state) => { state.loading = false; })
      .addCase(scheduleMassMessage.rejected,  (state) => { state.loading = false; })

      // ── fetchGroupMessageFilters ─────────────────────────────────────
      .addCase(fetchGroupMessageFilters.pending, (state) => {
        state.filterLoading = true;
        state.filterError   = "";
      })
      .addCase(fetchGroupMessageFilters.fulfilled, (state, action) => {
        state.filterLoading    = false;
        state.locationOptions  = action.payload.locationOptions;
        state.positionOptions  = action.payload.positionOptions;
        state.employeeOptions  = action.payload.employeeOptions;
        state.categories       = action.payload.categories;
      })
      .addCase(fetchGroupMessageFilters.rejected, (state, action) => {
        state.filterLoading = false;
        state.filterError   = action.payload ?? "Failed to load filters. Please refresh.";
      })

      // ── fetchMassSmsBudget ───────────────────────────────────────────
      .addCase(fetchMassSmsBudget.fulfilled, (state, action) => {
        state.smsBudget = action.payload;
      })

      // ── fetchMassMessageHistory ──────────────────────────────────────
      .addCase(fetchMassMessageHistory.pending, (state) => {
        state.history.loading = true;
        state.history.error   = "";
      })
      .addCase(fetchMassMessageHistory.fulfilled, (state, action) => {
        state.history.loading  = false;
        state.history.active   = action.payload.active   ?? [];
        state.history.inactive = action.payload.inactive ?? [];
      })
      .addCase(fetchMassMessageHistory.rejected, (state, action) => {
        state.history.loading = false;
        state.history.error   = action.payload ?? "Failed to load message history.";
      })

      // ── composeMassMessage ───────────────────────────────────────────
      // Holds the most-recent generated draft; UI keeps it visible in the
      // preview box until the user accepts ("Use this draft"), regenerates,
      // or discards via clearAiDraft. We keep `lastPrompt` so the regenerate
      // button knows what to re-send without the user re-typing.
      .addCase(composeMassMessage.pending, (state, action) => {
        state.aiCompose.loading      = true;
        state.aiCompose.error        = "";
        state.aiCompose.lastPrompt   = action.meta?.arg?.prompt ?? state.aiCompose.lastPrompt;
      })
      .addCase(composeMassMessage.fulfilled, (state, action) => {
        state.aiCompose.loading      = false;
        state.aiCompose.draft        = action.payload?.text ?? "";
        state.aiCompose.finishReason = action.payload?.finishReason ?? "";
        state.aiCompose.truncated    = !!action.payload?.truncated;
      })
      .addCase(composeMassMessage.rejected, (state, action) => {
        state.aiCompose.loading      = false;
        state.aiCompose.error        = action.payload ?? "Compose failed.";
      })

      // ── fetchAiTones ─────────────────────────────────────────────────
      // Choices arrive as a flat array of strings (e.g. ["Friendly", "Urgent"]).
      // We never overwrite a successfully-loaded list with [] on a later
      // failure — once we know the real tones, keep them until the next good
      // fetch — so a transient network blip doesn't hide the chips.
      .addCase(fetchAiTones.pending, (state) => {
        state.aiTones.loading = true;
        state.aiTones.error   = "";
      })
      .addCase(fetchAiTones.fulfilled, (state, action) => {
        state.aiTones.loading = false;
        state.aiTones.loaded  = true;
        state.aiTones.list    = action.payload ?? [];
      })
      .addCase(fetchAiTones.rejected, (state, action) => {
        state.aiTones.loading = false;
        state.aiTones.error   = action.payload ?? "Failed to load AI tones.";
      })

      // ── fetchMassMessageSends ────────────────────────────────────────
      // Per-parent state lives under sendsByGroupId[id]. UI uses the same
      // record id it already has from history, so the keying is natural.
      .addCase(fetchMassMessageSends.pending, (state, action) => {
        const id = action.meta?.arg?.groupMessageId;
        if (!id) return;
        const prev = state.sendsByGroupId[id] ?? { items: [] };
        state.sendsByGroupId[id] = { ...prev, loading: true, error: "" };
      })
      .addCase(fetchMassMessageSends.fulfilled, (state, action) => {
        const { groupMessageId, sends } = action.payload ?? {};
        if (!groupMessageId) return;
        state.sendsByGroupId[groupMessageId] = {
          loading: false,
          loaded:  true,
          error:   "",
          items:   sends ?? [],
        };
      })
      .addCase(fetchMassMessageSends.rejected, (state, action) => {
        const id = action.payload?.groupMessageId ?? action.meta?.arg?.groupMessageId;
        if (!id) return;
        const prev = state.sendsByGroupId[id] ?? { items: [] };
        state.sendsByGroupId[id] = {
          ...prev,
          loading: false,
          error:   action.payload?.message ?? "Failed to load send history.",
        };
      })

      // ── cancelMassMessage ────────────────────────────────────────────
      // Optimistic move: pull the cancelled record out of `active` and prepend
      // it to `inactive`. Mirror into BOTH buckets so the Admin page stays in
      // sync when an admin cancels their own message from the per-user panel.
      .addCase(cancelMassMessage.fulfilled, (state, action) => {
        const { recordId } = action.payload;
        for (const bucket of [state.history, state.adminHistory]) {
          const idx = bucket.active.findIndex((m) => m.id === recordId);
          if (idx >= 0) {
            const [cancelled] = bucket.active.splice(idx, 1);
            bucket.inactive.unshift({ ...cancelled, active: false, status: "Cancelled" });
          }
        }
      })

      // ── fetchAdminMassMessageHistory ─────────────────────────────────
      .addCase(fetchAdminMassMessageHistory.pending, (state) => {
        state.adminHistory.loading = true;
        state.adminHistory.error   = "";
      })
      .addCase(fetchAdminMassMessageHistory.fulfilled, (state, action) => {
        state.adminHistory.loading  = false;
        state.adminHistory.loaded   = true;
        state.adminHistory.active   = action.payload.active   ?? [];
        state.adminHistory.inactive = action.payload.inactive ?? [];
      })
      .addCase(fetchAdminMassMessageHistory.rejected, (state, action) => {
        state.adminHistory.loading = false;
        state.adminHistory.error   = action.payload ?? "Failed to load admin message history.";
      })

      // ── updateMassMessage ────────────────────────────────────────────
      // Optimistically merges edited fields into BOTH buckets. Next refetch
      // reconciles drift (e.g. server-recomputed Next Scheduled Time).
      .addCase(updateMassMessage.pending, (state, action) => {
        state.updatingId = action.meta?.arg?.recordId ?? null;
      })
      .addCase(updateMassMessage.fulfilled, (state, action) => {
        state.updatingId = null;
        const { recordId, fields } = action.payload ?? {};
        if (!recordId || !fields) return;
        const merge = (item) => ({
          ...item,
          message:          Object.prototype.hasOwnProperty.call(fields, "Message Context")           ? fields["Message Context"]          : item.message,
          aiPrompt:         Object.prototype.hasOwnProperty.call(fields, "AI Prompt")                 ? (fields["AI Prompt"] ?? "")       : item.aiPrompt,
          aiTone:           Object.prototype.hasOwnProperty.call(fields, "AI Tone")                   ? (fields["AI Tone"]   ?? "")       : item.aiTone,
          personalizePerRecipient: Object.prototype.hasOwnProperty.call(fields, "Personalize Per Recipient") ? !!fields["Personalize Per Recipient"] : item.personalizePerRecipient,
          recurring:        Object.prototype.hasOwnProperty.call(fields, "Recurring")                 ? !!fields["Recurring"]             : item.recurring,
          recurringEndDate: Object.prototype.hasOwnProperty.call(fields, "Recurring End Date")        ? fields["Recurring End Date"]      : item.recurringEndDate,
          daysOfWeek:       Object.prototype.hasOwnProperty.call(fields, "Day of the Week")           ? (fields["Day of the Week"] ?? []): item.daysOfWeek,
          scheduledTime:    Object.prototype.hasOwnProperty.call(fields, "Scheduled Time")            ? fields["Scheduled Time"]          : item.scheduledTime,
          nextScheduledTime:Object.prototype.hasOwnProperty.call(fields, "Next Scheduled Time")       ? fields["Next Scheduled Time"]     : item.nextScheduledTime,
        });
        for (const bucket of [state.history, state.adminHistory]) {
          for (const list of [bucket.active, bucket.inactive]) {
            const idx = list.findIndex((m) => m.id === recordId);
            if (idx >= 0) list[idx] = merge(list[idx]);
          }
        }
      })
      .addCase(updateMassMessage.rejected, (state) => {
        state.updatingId = null;
      })

      // ── fetchAdminMassMessageRecipients ──────────────────────────────
      // Per-message bucket keyed by recordId. Same lazy-load pattern as
      // sendsByGroupId — only resolved when the admin actually expands a
      // card, so a 50-row admin list doesn't fan out 50 BFF calls on mount.
      .addCase(fetchAdminMassMessageRecipients.pending, (state, action) => {
        const id = action.meta?.arg?.recordId;
        if (!id) return;
        const prev = state.recipientsByMessageId[id] ?? { recipients: [], count: 0 };
        state.recipientsByMessageId[id] = { ...prev, loading: true, error: "" };
      })
      .addCase(fetchAdminMassMessageRecipients.fulfilled, (state, action) => {
        const { recordId, recipients, count } = action.payload ?? {};
        if (!recordId) return;
        state.recipientsByMessageId[recordId] = {
          loading: false,
          loaded:  true,
          error:   "",
          recipients: recipients ?? [],
          count:      count ?? 0,
        };
      })
      .addCase(fetchAdminMassMessageRecipients.rejected, (state, action) => {
        const id = action.payload?.recordId ?? action.meta?.arg?.recordId;
        if (!id) return;
        const prev = state.recipientsByMessageId[id] ?? { recipients: [], count: 0 };
        state.recipientsByMessageId[id] = {
          ...prev,
          loading: false,
          error:   action.payload?.message ?? "Failed to resolve recipients.",
        };
      })

      // ── fetchPastMassMessageRecipients (Phase 5) ─────────────────────
      // Snapshotted recipients per-send for inactive cards. One bucket per
      // cron fire (sends array); most one-off messages have a single entry.
      .addCase(fetchPastMassMessageRecipients.pending, (state, action) => {
        const id = action.meta?.arg?.recordId;
        if (!id) return;
        const prev = state.pastRecipientsByMessageId[id] ?? { sends: [] };
        state.pastRecipientsByMessageId[id] = { ...prev, loading: true, error: "" };
      })
      .addCase(fetchPastMassMessageRecipients.fulfilled, (state, action) => {
        const { recordId, sendCount, sends } = action.payload ?? {};
        if (!recordId) return;
        state.pastRecipientsByMessageId[recordId] = {
          loading:   false,
          loaded:    true,
          error:     "",
          sendCount: sendCount ?? 0,
          sends:     sends ?? [],
        };
      })
      .addCase(fetchPastMassMessageRecipients.rejected, (state, action) => {
        const id = action.payload?.recordId ?? action.meta?.arg?.recordId;
        if (!id) return;
        const prev = state.pastRecipientsByMessageId[id] ?? { sends: [] };
        state.pastRecipientsByMessageId[id] = {
          ...prev,
          loading: false,
          error:   action.payload?.message ?? "Failed to load past recipients.",
        };
      });
  },
});

export const { clearAiDraft } = MassMessageSlice.actions;
export default MassMessageSlice.reducer;